import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  ApiResponse,
  ApiResponseWithItems,
  Invoice,
  InvoiceSearchParams,
} from "@/lib/types";

export const useGetInvoices = () => {
  return useQuery<ApiResponse<Invoice[]>>({
    queryKey: ["invoices"],
    queryFn: () => api.get("/invoices"),
  });
};

export const useGetInvoiceById = ({ id }: { id: string }) => {
  return useQuery<{ data: ApiResponse<Invoice> }>({
    queryKey: ["invoice", id],
    queryFn: () => api.get(`/invoices/${id}`),
    enabled: !!id,
  });
};

export const useGetInvoicesByVessel = (vesselId?: string, params?: string) => {
  return useQuery<ApiResponseWithItems<Invoice>>({
    queryKey: ["invoices", "invoices-by-vessel", "vessel", vesselId, params],
    queryFn: () =>
      api.get(`/invoices/search?VesselId=${vesselId}&${params ?? ""}`),
    enabled: !!vesselId,
  });
};

export const useSearchInvoices = (
  params: InvoiceSearchParams,
  { enabled }: { enabled: boolean } = { enabled: true }
) => {
  return useQuery<{ data: ApiResponseWithItems<Invoice> }>({
    queryKey: ["invoices", "invoices-search", "search", params],
    queryFn: () => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          searchParams.append(key, value.toString());
        }
      });

      const queryString = searchParams.toString();
      return api.get(`/invoices/search${queryString ? `?${queryString}` : ""}`);
    },
    enabled,
  });
};

export const useGetInvoicesBySearchParams = ({
  searchParams,
}: {
  searchParams: string;
}) => {
  return useQuery<ApiResponseWithItems<Invoice>>({
    queryKey: ["invoices", "search", searchParams],
    queryFn: () => api.get(`/invoices/search?${searchParams}`),
    enabled: !!searchParams,
  });
};

export const useGetOverdueInvoices = () => {
  return useQuery<ApiResponse<Invoice[]>>({
    queryKey: ["invoices", "overdue"],
    queryFn: () => api.get("/invoices/overdue"),
  });
};

export const useGetUpcomingDueInvoices = (daysAhead: number = 7) => {
  return useQuery<ApiResponse<Invoice[]>>({
    queryKey: ["invoices", "upcoming-due", daysAhead],
    queryFn: () => api.get(`/invoices/upcoming-due?daysAhead=${daysAhead}`),
  });
};
